/** Presentation helpers shared across features. */

const GRAMS = 'g'

export function formatNumber(
  value: number | null | undefined,
  digits = 0,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** Macro amounts: whole numbers on screen, whatever precision the source keeps. */
export function formatMacro(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return String(Math.round(value))
}

export function formatMacroGrams(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${formatMacro(value)}${GRAMS}`
}

export function formatGrams(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${formatNumber(value, Number.isInteger(value) ? 0 : 1)}${GRAMS}`
}

export function formatKcal(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${formatNumber(value, 0)} kcal`
}

/** ISO date to a short local date. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function daysSince(value: string | null | undefined): number | null {
  if (!value) return null
  const parsed = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((today.getTime() - parsed.getTime()) / 86_400_000)
}

/** "3 days ago", "in 2 months", "never". */
export function relativeFromNow(value: string | null | undefined): string {
  const days = daysSince(value)
  if (days === null) return 'never prepared'
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 0) return `in ${formatDuration(-days)}`
  if (days < 31) return `${days} days ago`
  if (days < 365) return `${Math.round(days / 30.44)} months ago`
  const years = days / 365
  return `${years < 10 ? years.toFixed(1) : Math.round(years)} years ago`
}

export function formatDuration(days: number): string {
  if (days < 31) return `${days} day${days === 1 ? '' : 's'}`
  if (days < 365) {
    const months = Math.round(days / 30.44)
    return `${months} month${months === 1 ? '' : 's'}`
  }
  const years = days / 365
  return `${years < 10 ? years.toFixed(1) : Math.round(years)} year${years >= 2 ? 's' : ''}`
}

/** Score components are shown to two decimals. */
export function formatScore(value: number): string {
  return value.toFixed(2)
}

export function pluralise(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`
}

/**
 * Calorie share of each macro, using the standard 4/4/9 factors. Returns null
 * when there is nothing to show.
 */
export function calorieSplit(protein: number, carbs: number, fat: number) {
  const proteinKcal = protein * 4
  const carbsKcal = carbs * 4
  const fatKcal = fat * 9
  const total = proteinKcal + carbsKcal + fatKcal
  if (total <= 0) return null
  return {
    protein: proteinKcal,
    carbs: carbsKcal,
    fat: fatKcal,
    proteinPct: (proteinKcal / total) * 100,
    carbsPct: (carbsKcal / total) * 100,
    fatPct: (fatKcal / total) * 100,
  }
}